import { useWasteStore } from '@waste/store/store';
import { UploadPhotoTemplate } from '@templates';
import { useUploadBlob } from '@waste/hooks/useUploadBlob';
import { useDeleteBlob } from '@waste/hooks/useDeleteBlob';
import { useSasTokenQuery } from '@waste/utils/sasToken/sasToken';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@waste/routes/Routes';
import { useEffect, useState } from 'react';
import { ErrorMessages } from '@types';
import { useInferImageData } from '@waste/hooks/useInferImageData';
import * as Sentry from '@sentry/react';
import { getInferImageCategory } from '@waste/utils/getInferImageCategory';
import { dataURLtoFile } from '@waste/utils/dataURLtoFile';
import { fileToBase64String } from '@waste/utils/fileToBase64String';

type WasteClassificationData = {
  classifications:
    | {
        confidence: number;
        label: string;
      }[]
    | undefined[];
  metadata: {
    latitude: number | null;
    longitude: number | null;
    timestamp: number | null;
  };
  number_of_bin_bags_group: number | null;
};

export function UploadPhoto() {
  const {
    wasteCategory,
    filename,
    blobUri,
    formattedInferCategory,
    updateIsImageMismatch,
    updateFormattedInferCategory,
    updateFilename,
    updateSelectedImage,
    blobEndpoint,
    selectedImage,
    updateBlobEndpoint,
    updateBlobUri,
    updateImageUrl,
  } = useWasteStore();

  const navigate = useNavigate();

  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [uploadedFile, setuploadedFile] = useState<File[] | undefined>([]);

  const handleUploadError = (error: string) => {
    if (error) {
      setErrorMessages([{ path: 'upload-photo', message: error }]);
    }
  };
  const handleFileChange = async (files: File[]) => {
    if (files === null) return;
    setuploadedFile(files);
    const selectedFile = files[0];
    await fileToBase64String(selectedFile);
    updateFilename(selectedFile.name);
    updateSelectedImage([selectedFile]);
    setErrorMessages([]);
  };

  useEffect(() => {
    if (filename && uploadedFile) {
      const fileStorage = sessionStorage.getItem('selectedFile')!;
      if (fileStorage) {
        const file = dataURLtoFile(JSON.parse(fileStorage), filename);
        if (file) {
          setuploadedFile([file]);
        }
      }
    }
  }, []);
  const { data: sasTokenData } = useSasTokenQuery();
  const data = filename?.length > 0 ? sasTokenData : undefined;
  const {
    mutate,
    data: uploadData,
    isPending: uploadPending,
  } = useUploadBlob();
  const handleUploadToBlob = async () => {
    if (selectedImage === undefined && filename === '') {
      setErrorMessages([]);
      updateBlobEndpoint('');
      updateBlobUri('');
      navigate(RoutesList.WASTE_DETAILS);
    }
    try {
      const singleImage: File | undefined = selectedImage?.find(
        (image: File) => image,
      );
      if (filename?.length > 0) {
        mutate({
          selectedImage: singleImage,
          data: data?.blob_endpoint as string,
        });

        updateBlobEndpoint(data?.blob_endpoint!);
        updateBlobUri(data?.blob_uri);
      }
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  // If the inferimage API fails, this data is passed to the getInferImageCategoryLabel function for users to continue the journey with the original waste category.
  const inferFailedResponseData = {
    classifications: [undefined],
    metadata: {
      latitude: null,
      longitude: null,
      timestamp: null,
    },
    number_of_bin_bags_group: null,
  };
  const { mutateAsync, isPending } = useInferImageData();
  const inferImageCategory = async () => {
    try {
      if (filename !== '' && blobUri !== '') {
        await mutateAsync(
          {
            image_uri: blobUri,
            filename: filename,
          },
          {
            onSuccess: (data) => {
              getInferImageCategoryLabel(data);
            },
          },
        );
      }
    } catch (error) {
      getInferImageCategoryLabel(inferFailedResponseData);
      Sentry.captureException(error);
    }
  };

  const getInferImageCategoryLabel = (data: WasteClassificationData) => {
    if (data.classifications[0] === undefined) {
      updateFormattedInferCategory(wasteCategory);
      updateIsImageMismatch(false);
    }
    const categoryData = data?.classifications[0]?.label;
    if (categoryData) {
      const infercategoryformat = getInferImageCategory(categoryData);
      updateFormattedInferCategory(infercategoryformat);
    }
    navigate(RoutesList.WASTE_DETAILS);
  };
  useEffect(() => {
    if (formattedInferCategory === '' && uploadData !== undefined) {
      inferImageCategory();
    }
  }, [uploadData]);

  const handleNextButton = async () => {
    try {
      updateSelectedImage(uploadedFile);
      await handleUploadToBlob();
      if (formattedInferCategory || !filename) {
        navigate(RoutesList.WASTE_DETAILS);
      }
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };
  const { mutate: deleteMutate } = useDeleteBlob();

  const handleDeleteFromBlob = async () => {
    try {
      if (blobEndpoint === undefined) return;
      deleteMutate(data?.blob_endpoint);
      updateFilename('');
      updateBlobEndpoint('');
      updateBlobUri('');
      setuploadedFile(undefined);
      updateSelectedImage(undefined);
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  const removePhoto = async () => {
    try {
      if (blobEndpoint?.length > 0) {
        await handleDeleteFromBlob();
      }
      updateImageUrl('');
      updateSelectedImage(undefined);
      updateFilename('');
      updateFormattedInferCategory('');
      setuploadedFile(undefined);
      updateSelectedImage(undefined);
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  const buttonPrevious = {
    id: 'previous',
    label: 'Previous',
    onClick: () => {
      navigate(`${RoutesList.WASTE_HOME}?type=${wasteCategory}`);
    },
  };

  const buttonNext = {
    id: 'next',
    label: 'I do not have a photo',
    loading: uploadPending || isPending,
    onClick: () => {
      handleNextButton();
    },
  };

  const Description = () => {
    return (
      <section
        data-testid="description"
        className="text-text-body font-weight text-bodyLg mt-5 lg:text-xl"
      >
        <p className="mt-5">This helps us locate and assess the issue.</p>
        <a
          className="text-action-link visited:text-action-link-visited hover:text-action-link-hover mt-2 inline-block underline"
          href="https://www.westminster.gov.uk/data-protection/report-it-image-disclaimer"
          target="_blank"
          rel="noopener noreferrer"
        >
          Find out how we will be using your image.
        </a>
        <p className="mt-7"> You can upload JPEG, PNG, GIF or WebP files.</p>
      </section>
    );
  };
  return (
    <UploadPhotoTemplate
      formTitle="Upload Photo"
      description={<Description />}
      handleFileChange={handleFileChange}
      handleUploadError={handleUploadError}
      errorPath={'upload-photo'}
      errorMessages={errorMessages}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      maxNumberOfUpload={1}
      handleDelete={removePhoto}
      id="upload-photo"
      initialFiles={uploadedFile}
    />
  );
}
